import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './TournamentPaymentModal.css';
import { useSound } from '../sfx/SoundProvider';

const API_ORIGIN = process.env.REACT_APP_API_URL || '';

export default function TournamentPaymentModal({
  tournament,
  onClose,
  onSuccess,
  apiBase = '/api/tournament',
  open = true, // родитель может передавать; но модалка также управляет собой
}) {
  const [visible, setVisible] = useState(Boolean(open));
  const [assets, setAssets] = useState({ tickets: [], skins: 0, balance: 0 });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
 const { play } = useSound();     
  // синхронизация с родителем (если он меняет open)
  useEffect(() => setVisible(Boolean(open)), [open]);

 const buyIn = Number(tournament?.buyIn ?? tournament?.buy_in ?? 0);
 const [feeInfo, setFeeInfo] = useState({ buyIn, neededSkins: 0, ticketsNeeded: 1 });
 const neededSkins = Number(feeInfo.neededSkins || 0);

  // билеты сгруппируем (для расчёта, но в UI — только нужный номинал)
  const ticketsByNominal = useMemo(() => {
    const map = new Map();
    (assets.tickets || []).forEach(({ value, quantity }) => {
      const v = Number(value);
      map.set(v, (map.get(v) || 0) + Number(quantity || 0));
    });
    return map;
  }, [assets.tickets]);

  const ticketsForBuyIn = ticketsByNominal.get(buyIn) || 0;
  const canPayByTicket = ticketsForBuyIn >= 1;
  const canPayBySkins = assets.skins >= neededSkins;

  const close = useCallback(() => {
    setVisible(false);       // закрываем сами
    onClose?.();             // и сообщаем родителю
  }, [onClose]);

  // ESC — закрыть (вешаем, только когда модалка видима)
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => e.key === 'Escape' && close();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [visible, close]);

  // загрузка активов (когда модалка открыта)
  useEffect(() => {
    if (!visible) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    axios
      .get(`${API_ORIGIN}${apiBase}/assets`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setAssets(res.data || { tickets: [], skins: 0, balance: 0 }))
      .catch(() => {});

      axios.get(`${API_ORIGIN}${apiBase}/feeInfo`, {
     params: { tournamentId: tournament.id },
     headers: { Authorization: `Bearer ${token}` },
   })
   .then(res => {
     const info = res.data || {};
     setFeeInfo({
       buyIn: Number(info.buyIn ?? buyIn),
       neededSkins: Number(info.neededSkins ?? 0),
       ticketsNeeded: Number(info.ticketsNeeded ?? 1),
     });
   })
   .catch(() => setFeeInfo({ buyIn, neededSkins: 0, ticketsNeeded: 1 }));

  }, [apiBase, visible]);

  const handlePayment = async (method) => {
    if (submitting) return;
    setError('');
    setSubmitting(true);

     // 🔊 звук на клик любого варианта оплаты
    play('allRegister').catch(() => {});
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // проверка
      const check = await axios.post(
        `${API_ORIGIN}${apiBase}/checkAccess`,
        { tournamentId: tournament.id, method, gameType: tournament?.gameType },
        { headers }
      );
      if (!check.data?.ok) {
        throw new Error(check.data?.error || 'Недостаточно активов');
      }

      // списание
      await axios.post(
        `${API_ORIGIN}${apiBase}/pay`,
        { tournamentId: tournament.id, method, gameType: tournament?.gameType },
        { headers }
      );

      onSuccess?.(method);
      close(); // закрыть после успешной оплаты
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Ошибка сервера');
    } finally {
      setSubmitting(false);
    }
  };

  // если не видима — вообще не рендерим
  if (!visible) return null;

  return (
    <div className="tpm-backdrop" onClick={close}>
      <div
        className="tpm-modal"
        onClick={(e) => e.stopPropagation()} // клики внутри не закрывают
        role="dialog"
        aria-modal="true"
      >
        <div className="tpm-header">
          <div className="tpm-title">Оплата турнира #{tournament.id}</div>
          <button type="button" className="tpm-close" aria-label="Закрыть" onClick={close}>
            ×
          </button>
        </div>

        <div className="tpm-subtitle">
          Buy-in: <span className="tpm-price">${buyIn}</span>
        </div>

        <div className="tpm-block">
          <button
            type="button"
            className="tpm-btn tpm-btn-primary"
            onClick={() => handlePayment('money')}
            disabled={submitting}
          >
            Оплатить деньгами
          </button>
        </div>

        <hr className="tpm-sep" />

        <div className="tpm-row">
          <div className="tpm-row-info">
            <div className="tpm-row-title">Билеты</div>
            <div className="tpm-row-sub">
              У вас <b>{ticketsForBuyIn}</b> билетов номиналом ${buyIn}
            </div>
          </div>

          <button
            type="button"
            className="tpm-btn tpm-btn-secondary"
            onClick={() => handlePayment('ticket')}
            disabled={!canPayByTicket || submitting}
            title={!canPayByTicket ? `Нужен 1 билет номиналом $${buyIn}` : undefined}
          >
            Оплатить билетом
          </button>
        </div>

        <hr className="tpm-sep" />

        <div className="tpm-row">
          <div className="tpm-row-info">
            <div className="tpm-row-title">Скины</div>
            <div className="tpm-row-sub">
              У вас <b>{assets.skins}</b> скинов • нужно: {neededSkins}
            </div>
          </div>

          <button
            type="button"
            className="tpm-btn tpm-btn-secondary"
            onClick={() => handlePayment('skin')}
            disabled={!canPayBySkins || submitting}
            title={!canPayBySkins ? `Нужно ${neededSkins} скинов` : undefined}
          >
            Оплатить скинами
          </button>
        </div>

        {error && <div className="tpm-error">{error}</div>}

        <div className="tpm-footer">
          <button type="button" className="tpm-btn tpm-btn-ghost" onClick={close} disabled={submitting}>
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}

// import React, { useEffect, useState } from 'react';
// import axios from 'axios';
// // import tournamentSocket from '../tournamentSocket'; // обязательно!

// const API_ORIGIN = process.env.REACT_APP_API_URL; // например: http://147.93.123.26:5000

// export default function TournamentPaymentModal({
//   tournament,
//   onClose,
//   onSuccess,
//   apiBase = '/api/tournament',
// }) {
//   const [assets, setAssets] = useState({ tickets: [], skins: 0 });
//   const [error, setError] = useState('');

//   useEffect(() => {
//     const token = localStorage.getItem('token');
//     if (!token) return;
//     axios.get(`${API_ORIGIN}${apiBase}/assets`, {
//       headers: { Authorization: `Bearer ${token}` },
//     })
//     .then(res => setAssets(res.data))
//     .catch(err => console.error('Ошибка загрузки ресурсов:', err));
//   }, [apiBase]);

//   const handlePayment = async (method) => {
//     setError('');
//     try {
//       const token = localStorage.getItem('token');

//       // 1) проверка
//       const res = await axios.post(`${API_ORIGIN}${apiBase}/checkAccess`, {
//         tournamentId: tournament.id,
//         method,
//         gameType: tournament?.gameType,
//       }, {
//         headers: { Authorization: `Bearer ${token}` },
//       });

//       if (res.data.ok) {
//         // 2) списание
//         await axios.post(`${API_ORIGIN}${apiBase}/pay`, {
//           tournamentId: tournament.id,
//           method,
//           gameType: tournament?.gameType,
//         }, {
//           headers: { Authorization: `Bearer ${token}` },
//         });

//         onSuccess(method);
//       }
//     } catch (err) {
//       setError(err.response?.data?.error || 'Ошибка сервера');
//     }
//   };


//   return (
//     <div className="modal-backdrop">
//       <div className="modal-content" style={{ position: 'fixed', inset: 0, zIndex: 10000 }}>
//         <h2>Оплата турнира #{tournament.id}</h2>
//         <p>Buy-in: {tournament.buyIn}$</p>

//         <div className="payment-option">
//           <button onClick={() => handlePayment('money')}>Оплатить деньгами</button>
//         </div>

//         <div className="payment-option">
//           <span>У вас {assets.tickets?.find(t => t.value === tournament.buyIn)?.quantity || 0} билетов / {tournament.buyIn}</span>
//           <button onClick={() => handlePayment('ticket')}>Оплатить билетом</button>
//         </div>

//         <div className="payment-option">
//           <span>У вас {assets.skins} скинов / {tournament.buyIn * 6}</span>
//           <button onClick={() => handlePayment('skin')}>Оплатить скинами</button>
//         </div>

//         {error && <p style={{ color: 'red' }}>{error}</p>}
//         <button onClick={onClose}>Отмена</button>
//       </div>
//     </div>
//   );
// }






// import React, { useEffect, useState } from 'react';
// import axios from 'axios';
// import tournamentSocket from '../tournamentSocket'; // обязательно!

// export default function TournamentPaymentModal({ tournament, onClose, onSuccess }) {
//   const [assets, setAssets] = useState({ tickets: [], skins: 0 });
//   const [error, setError] = useState('');












//   useEffect(() => {
//     const token = localStorage.getItem('token');
//     if (!token) return;
//     axios.get('http://localhost:5000/api/tournament/assets', {
//       headers: { Authorization: `Bearer ${token}` }
//     })
//       .then(res => setAssets(res.data))
//       .catch(err => console.error('Ошибка загрузки ресурсов:', err));
//   }, []);

// const handlePayment = async (method) => {
//   setError('');
//   try {
//     const token = localStorage.getItem('token');

//     // 1. Проверка доступа
//     const res = await axios.post('http://localhost:5000/api/tournament/checkAccess', {
//       tournamentId: tournament.id,
//       method
//     }, {
//       headers: { Authorization: `Bearer ${token}` }
//     });

//     if (res.data.ok) {
//       // 2. Списание после успешной проверки
//       await axios.post('http://localhost:5000/api/tournament/pay', {
//         tournamentId: tournament.id,
//         method
//       }, {
//         headers: { Authorization: `Bearer ${token}` }
//       });

//       // 3. Уведомляем родительский компонент
//       onSuccess(method);
//     }

//   } catch (err) {
//     setError(err.response?.data?.error || 'Ошибка сервера');
//   }
// };


//   return (
//     <div className="modal-backdrop">
//       <div className="modal-content">
//         <h2>Оплата турнира #{tournament.id}</h2>
//         <p>Buy-in: {tournament.buyIn}$</p>

//         <div className="payment-option">
//           <button onClick={() => handlePayment('money')}>Оплатить деньгами</button>
//         </div>

//         <div className="payment-option">
//           <span>У вас {assets.tickets?.find(t => t.value === tournament.buyIn)?.quantity || 0} билетов / {tournament.buyIn}</span>
//           <button onClick={() => handlePayment('ticket')}>Оплатить билетом</button>
//         </div>

//         <div className="payment-option">
//           <span>У вас {assets.skins} скинов / {tournament.buyIn * 6}</span>
//           <button onClick={() => handlePayment('skin')}>Оплатить скинами</button>
//         </div>

//         {error && <p style={{ color: 'red' }}>{error}</p>}
//         <button onClick={onClose}>Отмена</button>
//       </div>
//     </div>
//   );
// }
